'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UpOutlined, DownOutlined, DownloadOutlined } from '@ant-design/icons';
import { Image } from 'antd';
import { API_BASE_URL } from '../lib/constans';

export default function YachtItineraries({ itinerariesRawData }) {

    if (!Array.isArray(itinerariesRawData) || itinerariesRawData.length === 0) {
        return null;
    }

    const groupedItineraries = itinerariesRawData.reduce((acc, item) => {
        const itinerary = item.itinerary_yacht;
        if (!itinerary) return acc;

        const routeName = itinerary.route || "General Route";

        if (!acc[routeName]) {
            acc[routeName] = [];
        }

        acc[routeName].push({
            id: item.id,
            duration: itinerary.name,
            returns: itinerary.return,
            departures: itinerary.departure,
            islands: itinerary.islandsIncluded || [],
            downloadUrl: itinerary.file,
            mapImage: itinerary.image
        });

        return acc;
    }, {});

    const routes = Object.keys(groupedItineraries);
    const [activeRoute, setActiveRoute] = useState(routes[0] || '');
    const [openAccordionId, setOpenAccordionId] = useState(null);

    const currentItineraries = groupedItineraries[activeRoute] || [];

    useEffect(() => {
        if (currentItineraries.length > 0) {
            setOpenAccordionId(currentItineraries[0].id);
        }
    }, [activeRoute]);

    const toggleAccordion = (id) => {
        setOpenAccordionId(openAccordionId === id ? null : id);
    };

    const activeItineraryData = currentItineraries.find(i => i.id === openAccordionId) || currentItineraries[0];
    const currentMapImage = activeItineraryData?.mapImage || '/images/galapagos-map.png';

    if (routes.length === 0) return null;

    return (
        <section className="w-full max-w-6xl mx-auto px-4 py-12 font-sans text-p-blue">

            <div className="text-center mb-6">
                <h2 className="text-2xl md:text-3xl font-bold tracking-wider inline-block relative pb-2 uppercase border-b-4 border-p-orange">
                    Galapagos Cruise Itineraries
                </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-5 w-full">
                    <div className="flex flex-wrap gap-4 md:gap-6 border-b border-gray-200 pb-3 mb-6">
                        {routes.map((route) => {
                            const isActive = route === activeRoute;
                            return (
                                <button
                                    aria-label="Route"
                                    key={route}
                                    onClick={() => setActiveRoute(route)}
                                    className={`relative pb-1 text-sm md:text-base font-bold tracking-wide transition-colors duration-300 cursor-pointer
                                                ${isActive ? 'text-p-orange/70' : 'text-p-orange/70 hover:text-p-orange/70'}`}
                                >
                                    {route}
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeRouteLine"
                                            className="absolute bottom-0 left-0 right-0 h-[3px] bg-p-blue"
                                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    <div className="space-y-4">
                        {currentItineraries.map((itinerary) => {
                            const isOpen = openAccordionId === itinerary.id;

                            return (
                                <div
                                    key={itinerary.id}
                                    className="bg-[#F5EFEA]/60 rounded-2xl border border-[#F5EFEA] overflow-hidden transition-all duration-300"
                                >
                                    <button
                                        aria-label="itinerary"
                                        onClick={() => toggleAccordion(itinerary.id)}
                                        className="w-full flex items-center justify-between p-5 text-left text-lg md:text-xl font-bold text-p-orange/70"
                                    >
                                        <span>{itinerary.duration}</span>
                                        <div className="w-8 h-8 rounded-full bg-p-blue text-white flex items-center justify-center text-xl cursor-pointer">
                                            {isOpen ? <UpOutlined /> : <DownOutlined />}
                                        </div>
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                            >
                                                <div className="px-3 pb-5 pt-1 border-t border-p-orange text-p-blue mx-6">
                                                    <div className="space-y-1 mb-4 text-base md:text-lg">
                                                        <p><span className="text-p-blue">Returns on</span> <span className="font-semibold">{itinerary.returns}</span></p>
                                                        <p><span className="text-p-blue">Departures on</span> <span className="font-semibold">{itinerary.departures}</span></p>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-x-2 gap-y-3 mb-5">
                                                        {itinerary.islands.map((island, idx) => (
                                                            <div key={idx} className="flex items-center gap-2 text-sm md:text-base">
                                                                <span className="w-4 h-4 rounded-full border-2 border-dashed border-p-orange/70 flex items-center justify-center p-[2px]">
                                                                    <span className="w-1 h-1 rounded-full bg-p-orange/70" />
                                                                </span>
                                                                <span>{island}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="border-t border-p-orange pt-4 flex justify-end cursor-pointer*">
                                                        <a
                                                            href={API_BASE_URL + itinerary.downloadUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            aria-label="Download Itinerary"
                                                            download
                                                            className="inline-flex items-center gap-2 text-sm md:text-base  hover:text-p-orange/70 transition-colors"
                                                        >
                                                            Download Itinerary <DownloadOutlined className="text-base" />
                                                        </a>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="lg:col-span-7 w-full flex justify-center lg:justify-end sticky top-24">
                    <div className="relative w-full max-w-[550px] aspect-square flex items-center justify-center overflow-hidden">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentMapImage}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ duration: 0.3 }}
                                className="w-full h-full flex items-center justify-center cursor-pointer header-preview-map"
                            >
                                <Image
                                    src={API_BASE_URL + currentMapImage}
                                    alt={`Map for ${activeRoute}`}
                                    className="w-full h-full object-contain"
                                    preview={true}
                                />
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>

            </div>
        </section>
    );
}